import React from 'react';
import Type from 'prop-types';
import { noop } from 'lodash';
import {
  Box,
  ButtonBase,
  FormControl,
  FormHelperText,
  Typography,
} from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { t } from 'utils/i18n';
import ListItem from '../../../../List/ListItem';
import ListItemText from '../../../../List/ListItemText';
import ListItemSecondaryAction from '../../../../List/ListItemSecondaryAction';
import styles from './ListPickerButton.module.css';

const ListPickerButton = (props) => {
  const {
    id,
    label,
    options,
    limit,
    emptyText,
    helperText,
    getOptionLabel,
    disableGutters,
    disabled,
    ContainerComponent,
    Component,
    FormControlProps,
    InputLabelProps,
    FormHelperTextProps,
    onClick,
    appearance,
  } = props;

  const inputLabelId = label && id ? `${id}-label` : undefined;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;

  const renderSelectedOptions = () => {
    if (options.length === 0) {
      return emptyText;
    }

    return options.slice(0, limit).map(getOptionLabel).join(', ');
  };

  if (appearance === 'filter') {
    return (
      <FormControl {...FormControlProps}>
        <ButtonBase
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          sx={{
            m: -2,
            p: 2,
            width: 'calc(100% + 32px)',
            textAlign: 'left',
            borderRadius: '16px',
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Typography component="span" data-filter-label>
              {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 16,
                  fontWeight: 500,
                  overflowWrap: 'anywhere',
                }}
              >
                {renderSelectedOptions()}
              </Typography>
              <ChevronRightIcon
                sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 20 }}
              />
            </Box>
          </Box>
        </ButtonBase>
        {helperText && (
          <FormHelperText {...FormHelperTextProps}>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl {...FormControlProps}>
      <div
        id={inputLabelId}
        className={styles.inputLabel}
        disableAnimation
        {...InputLabelProps}
      >
        <span className={styles.title}>{label}</span>
      </div>

      <ListItem
        component={Component}
        ContainerComponent={ContainerComponent}
        disableGutters={disableGutters}
        disabled={disabled}
        sx={{ justifyContent: 'flex-start' }}
        button
        onClick={onClick}
      >
        <ListItemText
          primary={renderSelectedOptions()}
          sx={{ whiteSpace: 'nowrap' }}
          className={styles.buttonText}
        />
        <ListItemSecondaryAction>
          <ChevronRightIcon />
        </ListItemSecondaryAction>
      </ListItem>

      {helperText && (
        <FormHelperText
          id={helperTextId}
          className={styles.formHelperText}
          {...FormHelperTextProps}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

ListPickerButton.propTypes = {
  id: Type.string,
  label: Type.string,
  // eslint-disable-next-line react/forbid-prop-types
  options: Type.arrayOf(Type.oneOfType([Type.string, Type.object])),
  getOptionLabel: Type.func,
  limit: Type.number,
  helperText: Type.string,
  disableGutters: Type.bool,
  disabled: Type.bool,
  emptyText: Type.string,
  Component: Type.node,
  ContainerComponent: Type.node,
  /**
   * See [FormControl](https://material-ui.com/api/form-control/)
   */
  FormControlProps: Type.object, // eslint-disable-line react/forbid-prop-types
  /**
   * See [InputLabel](https://material-ui.com/api/input-label/)
   */
  InputLabelProps: Type.object, // eslint-disable-line react/forbid-prop-types
  /**
   * See [FormHelperText](https://material-ui.com/api/form-helper-text/)
   */
  FormHelperTextProps: Type.object, // eslint-disable-line react/forbid-prop-types
  onClick: Type.func,
  appearance: Type.oneOf(['default', 'filter']),
};

ListPickerButton.defaultProps = {
  id: undefined,
  label: undefined,
  options: [],
  limit: 2,
  emptyText: t('select'),
  helperText: undefined,
  disableGutters: true,
  disabled: false,
  Component: 'div',
  ContainerComponent: 'div',
  FormControlProps: {},
  InputLabelProps: {},
  FormHelperTextProps: {},
  getOptionLabel: noop,
  onClick: noop,
  appearance: 'default',
};

export default ListPickerButton;
